import { INLINES } from '@contentful/rich-text-types';
import React, { FC } from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useSelector } from 'react-redux';

import NormalButton from '@/components/button/normal';
import Input from '@/components/form-elements/input';
import RichTextRenderer, {
  renderInlineHyperlink,
} from '@/components/rich-text-renderer';
import getThemedButtonProps from '@/lib/get-themed-button-props';
import getVariablePlainText from '@/lib/get-variable-plain-text';
import useVariables from '@/lib/hooks/use-variables';
import { selectGlobalPageTheme } from '@/store/slices/global';
import { NewsletterStaticVariant } from '@/types/views/sections';

import getVariantVariableKey from '../../lib/get-variant-variable-key';
import { NewsletterFormInputData } from '../../lib/newsletter-form-data';
import styles from './styles';

const DEFAULT_PRIVACY_POLICY_URL = 'https://energy.ch/datenschutz';
const DEFAULT_PRIVACY_POLICY_ERROR_TEXT =
  'Bitte akzeptiere die Datenschutzbestimmungen.';

type SecondStepProps = {
  register: UseFormRegister<NewsletterFormInputData>;
  errors: FieldErrors;
  buttonDisabled: boolean;
  onNextButtonClick: () => void;
  onPrevButtonClick: () => void;
  inputValues: {
    firstName: string;
    lastName: string;
  };
  newsletterVariant?: NewsletterStaticVariant;
};

const SecondStep: FC<SecondStepProps> = ({
  register,
  errors,
  buttonDisabled,
  onPrevButtonClick,
  onNextButtonClick,
  inputValues,
  newsletterVariant = 'newsletter',
}) => {
  const variables = useVariables();
  const pageTheme = useSelector(selectGlobalPageTheme);
  const buttonProps = getThemedButtonProps(pageTheme);
  const key = (suffix: string) =>
    getVariantVariableKey(newsletterVariant, suffix);

  const title = getVariablePlainText(variables[key('enter-name-text')]);
  const text = variables[key('datenschutz-text')];
  const firstName = getVariablePlainText(variables[key('input-name-text')]);
  const lastName = getVariablePlainText(variables[key('input-surname-text')]);
  const ctaBtnText = getVariablePlainText(variables[key('text')]);
  const cancelBtnText = getVariablePlainText(variables[key('cancel-text')]);
  const firstNameErrorText = getVariablePlainText(
    variables['first-name-error-text'],
  );
  const lastNameErrorText = getVariablePlainText(
    variables['last-name-error-text'],
  );
  const privacyPolicyText = variables[key('privacy-checkbox-label')];
  const privacyPolicyErrorText = getVariablePlainText(
    variables[key('datenschutz-error-text')],
  );

  const privacyPolicyError = errors?.privacyPolicy?.message;

  const privacyPolicyLabel = privacyPolicyText ? (
    <RichTextRenderer
      body={privacyPolicyText}
      options={{
        renderNode: {
          [INLINES.HYPERLINK]: renderInlineHyperlink(styles.checkboxLink, true),
        },
      }}
    />
  ) : (
    <p>
      Ich habe die{' '}
      <a
        href={DEFAULT_PRIVACY_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.checkboxLink}
      >
        Datenschutzbestimmungen
      </a>{' '}
      gelesen und akzeptiere diese.
    </p>
  );

  return (
    <div>
      <h2 className={styles.heading}>{title}</h2>

      <RichTextRenderer
        body={text}
        className={styles.text}
        options={{
          renderNode: {
            [INLINES.HYPERLINK]: renderInlineHyperlink(styles.text),
          },
        }}
      />

      <div className={styles.formGroup}>
        <Input
          {...register('firstName', {
            required: firstNameErrorText || 'First name is required',
          })}
          id="firstName"
          label={firstName || 'Vorname'}
          type="text"
          placeholder={firstName || 'Vorname'}
          containerClassName={styles.inputWrapper}
          className={styles.input(!!inputValues.firstName)}
          error={errors?.firstName?.message}
        />

        <Input
          {...register('lastName', {
            required: lastNameErrorText || 'Last name is required',
          })}
          id="lastName"
          label={lastName || 'Nachname'}
          type="text"
          placeholder={lastName || 'Nachname'}
          containerClassName={styles.inputWrapper}
          className={styles.input(!!inputValues.lastName)}
          error={errors?.lastName?.message}
        />

        <NormalButton
          {...buttonProps}
          text={ctaBtnText}
          size="L"
          className={styles.button}
          onClick={onNextButtonClick}
          disabled={buttonDisabled}
        />
      </div>

      <div className={styles.inputWrapper}>
        <div className={styles.checkboxGroup}>
          <input
            {...register('privacyPolicy', {
              required:
                privacyPolicyErrorText || DEFAULT_PRIVACY_POLICY_ERROR_TEXT,
            })}
            type="checkbox"
            id="privacyPolicy"
            className={styles.checkbox}
            aria-describedby={
              privacyPolicyError ? 'privacyPolicyError' : undefined
            }
          />

          <label htmlFor="privacyPolicy" className={styles.checkboxLabel}>
            {privacyPolicyLabel}
          </label>
        </div>

        {privacyPolicyError && (
          <p id="privacyPolicyError" role="alert" className={styles.errorText}>
            {privacyPolicyError}
          </p>
        )}
      </div>

      <div className={styles.btnContainer}>
        <button
          type="button"
          onClick={onPrevButtonClick}
          className={styles.cancelBtn}
        >
          {cancelBtnText}
        </button>
      </div>
    </div>
  );
};

export default SecondStep;
